import type { PresentationOptionV2 } from 'paintscout'
import { movePresentationNextBlock, movePresentationNextSection } from '@paintscout/util/builder'

/**
 * react-beautiful-dnd droppable types. Two independent drags exist in the editor: blocks move
 * between sections, sections move between pages. Separate types stop a block being dropped into a
 * page's section list and vice versa.
 */
export const PRESENTATION_BLOCK_DND_TYPE = 'presentation-block'
export const PRESENTATION_SECTION_DND_TYPE = 'presentation-section'

/** The subset of react-beautiful-dnd's DropResult this module needs. */
export interface PresentationDragResult {
  type?: string
  draggableId: string
  source: { droppableId: string; index: number }
  destination?: { droppableId: string; index: number } | null
}

/**
 * Applies a drop to the presentation, or returns `null` when there's nothing to do (dropped outside
 * any droppable, or back in its original spot).
 *
 * Purely a translation layer: a droppableId is the container's key (a section key for a block drag,
 * a page key for a section drag) and draggableId is the moved node's key, so the actual mutation is
 * delegated to the tree helpers in @paintscout/util. Those address nodes by key rather than by index
 * path, which is what makes a stale dnd result a safe no-op instead of a wrong move — so this
 * module no longer needs the invariant-violation logging the index-based version carried.
 */
export function applyPresentationDragResult(
  presentation: PresentationOptionV2,
  result: PresentationDragResult
): PresentationOptionV2 | null {
  const { type, draggableId, source, destination } = result

  if (!destination) {
    return null
  }
  if (source.droppableId === destination.droppableId && source.index === destination.index) {
    return null
  }

  if (type === PRESENTATION_SECTION_DND_TYPE) {
    return movePresentationNextSection({
      presentation,
      sectionKey: draggableId,
      toPageKey: destination.droppableId,
      toIndex: destination.index
    })
  }

  return movePresentationNextBlock({
    presentation,
    blockKey: draggableId,
    toSectionKey: destination.droppableId,
    toIndex: destination.index
  })
}

/**
 * Whether a block's page-reorder drag handle is currently disabled.
 *
 * Presentation.tsx spreads @hello-pangea/dnd's `dragHandleProps` onto the block's whole
 * `<section>`, so the entire block surface is the drag handle. Its window-level mouse sensor
 * starts a block drag after ~5px of movement, which beats any drag the block's own content
 * wants to run — react-sortable-hoc's photo reorder in a Gallery waits 300ms before it starts
 * (#23710), and the Slider Image divider is a custom element the sensor never treats as
 * interactive (#23232).
 *
 * Two things therefore disable the handle:
 * - `dragSuspendedBlockKey`, set by a block through `setDragSuspended` while the cursor is over
 *   its own interactive content.
 * - `focusedBlock`, the block the user has clicked into to edit. Its inline editors own the
 *   pointer, so block reorder yields to them. Reorder still works from any unfocused block and
 *   from the builder tree.
 *
 * A falsy `blockKey` is never disabled: both keys are `''` when nothing is suspended or focused.
 */
export function isBlockDragDisabled({
  blockKey,
  dragSuspendedBlockKey,
  focusedBlock
}: {
  blockKey: string
  dragSuspendedBlockKey?: string
  focusedBlock?: string
}): boolean {
  if (!blockKey) return false

  return dragSuspendedBlockKey === blockKey || focusedBlock === blockKey
}
